import { EiCategory, EI_CATEGORY_RATES, HouseholdSituation, TaxStrategy, TreasuryProfile, computeHouseholdParts } from '@shared/types';
import { FinancialConfidenceLevel, selectFinancialConfidence } from './financialConfidence';

export interface FiscalScenarioInput {
  status: TreasuryProfile['status'];
  revenue: number;
  charges: number;
  caYtd?: number;
  vatRegistered?: boolean;
  taxStrategy?: TaxStrategy;
  salaryNetTarget?: number;
  dividendPayoutPct?: number;
  eiCategory?: EiCategory;
  acre?: boolean;
  versementLiberatoire?: boolean;
  householdSituation?: HouseholdSituation;
  children?: number;
  dividendTaxOption?: 'pfu' | 'bareme';
  regimeReel?: boolean;
  managementFeesOverride?: number;
}

export interface FiscalBreakdown {
  status: TreasuryProfile['status'];
  strategy: TaxStrategy;
  revenue: number;
  charges: number;
  operatingProfit: number;
  managementFees: number;
  payrollCost: number;
  socialContributions: number;
  netSalaryEstimate: number;
  corporateTax: number;
  reducedCorporateTax: number;
  standardCorporateTax: number;
  distributableAfterCorporateTax: number;
  dividendGross: number;
  pfu: number;
  dividendNet: number;
  estimatedTakeHome: number;
  estimatedTakeHomeAfterIr: number;
  estimatedVatRemaining: number | null;
  estimatedCfe: number;
  taxePuma: number;
  eiSocialRate: number;
  eiCfpAmount: number;
  acreReduction: number;
  versementLiberatoireAmount: number;
  householdParts: number;
  incomeTaxBrut: number;
  incomeTaxDecote: number;
  incomeTaxNet: number;
  householdSituation: HouseholdSituation | null;
  children: number;
  assumptions: string[];
  fillonReduction: number;
  dividendTaxOption: 'pfu' | 'bareme';
  prelevementsSociaux: number;
  cotisationMinSSI: number;
  regimeReel: boolean;
  tauxMarginalIR: number;
  resteAvantTrancheSuivanteIR: number;
  microDepassementSeuil: boolean;
  /** LOT 7E — niveau de confiance de cette estimation (voir financialConfidence.ts). */
  confidence: FinancialConfidenceLevel;
}

// Seuils franchise en base de TVA, en vigueur depuis le 01/07/2026 (loi
// n° 2025-1044 du 03/11/2025 ; BOI-TVA-DECLA-40-10-10-20260701, BOFiP
// vérifié le 14/09/2026 — voir docs/HUB-LOT7D-REGULATORY-CLOSURE.md, §2/§3).
// Remplace les anciens seuils "2024" (91 900 / 101 000 / 36 800 / 39 100).
export const VAT_THRESHOLD_BIC = 85000;
export const VAT_THRESHOLD_BIC_MAJOREE = 93500;
export const VAT_THRESHOLD_BNC = 37500;
export const VAT_THRESHOLD_BNC_MAJOREE = 41250;

// Plafonds régime micro 2026-2028 (au-delà → régime réel obligatoire).
// LOT 7C — vérifié le 13/09/2026 sur entreprendre.service-public.gouv.fr
// (voir docs/HUB-LOT7C-REGULATORY-MARKET.md, §3) : anciennes valeurs 2025
// (188 700 / 77 700 / 77 700), remplacées par les seuils 2026-2028.
export const MICRO_PLAFOND_BIC_VENTE = 203100;
export const MICRO_PLAFOND_BIC_SERVICES = 83600;
export const MICRO_PLAFOND_BNC = 83600;

const CORPORATE_TAX_REDUCED_LIMIT = 42500;
const CORPORATE_TAX_REDUCED_RATE = 0.15;
const CORPORATE_TAX_STANDARD_RATE = 0.25;
const PFU_RATE = 0.314; // 12,8 % IR + 18,6 % PS — vérifié à jour 2026 (LOT 7C, §4)
// LOT 7C — prélèvements sociaux sur dividendes (option barème progressif) :
// 17,2 % → 18,6 % depuis la LFSS 2026 (hausse de la CSG sur revenus du
// capital, 9,2 % → 10,6 %). Voir docs/HUB-LOT7C-REGULATORY-MARKET.md, §4.
const PS_RATE = 0.186;

// LOT 7C — SMIC mensuel brut : 1 766,92 € (valeur 2024) → 1 867,02 €
// (revalorisation du 1er juin 2026). Voir docs/HUB-LOT7C-REGULATORY-MARKET.md, §3.
const SMIC_MENSUEL = 1867.02;
const FILLON_MAX_RATE = 0.3194;
const COTISATION_MIN_SSI_ANNUELLE = 1298;

// Abattements forfaitaires micro-BIC / micro-BNC pour calcul de la base IR
const MICRO_ABATTEMENTS: Record<EiCategory, number> = {
  'bic-vente': 0.71,
  'bic-services': 0.50,
  'bnc-liberal': 0.34,
  'bnc-cipav': 0.34,
};

const IR_BRACKETS: Array<{ min: number; max: number; rate: number }> = [
  { min: 0, max: 11600, rate: 0 },
  { min: 11601, max: 29579, rate: 0.11 },
  { min: 29580, max: 84577, rate: 0.30 },
  { min: 84578, max: 181917, rate: 0.41 },
  { min: 181918, max: Infinity, rate: 0.45 },
];

const QF_PLAFOND_STANDARD = 1807;
const QF_PLAFOND_PARENT_ISOLE = 4262;
const DECOTE_SEUIL_CELIB = 1982;
const DECOTE_SEUIL_COUPLE = 3277;
const DECOTE_BASE_CELIB = 897;
const DECOTE_BASE_COUPLE = 1483;
const DECOTE_PCT = 0.4525;

function clampPositive(value: number): number {
  return Number.isFinite(value) ? Math.max(0, value) : 0;
}

function computeMarginalRate(revenuImposable: number, parts: number): { tauxMarginalIR: number; resteAvantTrancheSuivanteIR: number } {
  if (revenuImposable <= 0 || parts <= 0) return { tauxMarginalIR: 0, resteAvantTrancheSuivanteIR: 0 };
  const revParPart = revenuImposable / parts;
  for (let i = IR_BRACKETS.length - 1; i >= 0; i--) {
    const b = IR_BRACKETS[i];
    if (revParPart > b.min) {
      const resteParPart = b.max === Infinity ? 0 : Math.max(0, b.max - revParPart);
      return { tauxMarginalIR: b.rate, resteAvantTrancheSuivanteIR: Math.round(resteParPart * parts) };
    }
  }
  return { tauxMarginalIR: 0, resteAvantTrancheSuivanteIR: 0 };
}

function applyBrackets(revenu: number): number {
  if (revenu <= 0) return 0;
  let tax = 0;
  for (const b of IR_BRACKETS) {
    if (revenu <= b.min) continue;
    const tranche = Math.min(revenu, b.max) - b.min;
    if (tranche > 0) tax += tranche * b.rate;
  }
  return tax;
}

export function computeIncomeTax(revenuImposable: number, parts: number, situation: HouseholdSituation, children: number): { impotBrut: number; decote: number; impotNet: number } {
  if (revenuImposable <= 0 || parts <= 0) return { impotBrut: 0, decote: 0, impotNet: 0 };

  const impotAvantPlafond = applyBrackets(revenuImposable / parts) * parts;
  const baseParts = situation === 'celibataire' || situation === 'parent-isole' ? 1 : 2;
  const extraHalfPoints = Math.max(0, (parts - baseParts) / 0.5);

  let impotBrut: number;
  if (extraHalfPoints > 0) {
    const impotSansEnfants = applyBrackets(revenuImposable / baseParts) * baseParts;
    const avantageReel = Math.max(0, impotSansEnfants - impotAvantPlafond);
    let plafondTotal = 0;
    for (let i = 0; i < extraHalfPoints; i++) {
      plafondTotal += (situation === 'parent-isole' && i === 0) ? QF_PLAFOND_PARENT_ISOLE : QF_PLAFOND_STANDARD;
    }
    impotBrut = Math.max(0, impotSansEnfants - Math.min(avantageReel, plafondTotal));
  } else {
    impotBrut = impotAvantPlafond;
  }

  const isCouple = situation === 'couple' || (situation === 'veuf' && children > 0);
  const seuilDecote = isCouple ? DECOTE_SEUIL_COUPLE : DECOTE_SEUIL_CELIB;
  const baseDecote = isCouple ? DECOTE_BASE_COUPLE : DECOTE_BASE_CELIB;
  const decote = impotBrut < seuilDecote ? Math.max(0, baseDecote - DECOTE_PCT * impotBrut) : 0;
  const impotNet = Math.max(0, impotBrut - decote);

  return { impotBrut, decote, impotNet };
}

function resolveStrategy(status: TreasuryProfile['status'], strategy?: TaxStrategy): TaxStrategy {
  if (strategy) return strategy;
  if (status === 'portage' || status === 'ei') return 'remuneration';
  if (status === 'eurl') return 'mixte';
  return 'dividendes';
}

function computeCorporateTax(base: number) {
  const taxableBase = clampPositive(base);
  const reducedBase = Math.min(taxableBase, CORPORATE_TAX_REDUCED_LIMIT);
  const standardBase = Math.max(0, taxableBase - CORPORATE_TAX_REDUCED_LIMIT);
  const reducedCorporateTax = reducedBase * CORPORATE_TAX_REDUCED_RATE;
  const standardCorporateTax = standardBase * CORPORATE_TAX_STANDARD_RATE;
  return { reducedCorporateTax, standardCorporateTax, corporateTax: reducedCorporateTax + standardCorporateTax };
}

function estimateCfe(status: TreasuryProfile['status'], revenue: number): number {
  if (revenue <= 0 || revenue < 5000) return 0;
  if (status === 'ei') return Math.min(800, Math.max(200, revenue * 0.004));
  return Math.min(1200, Math.max(400, revenue * 0.006));
}

// Réduction Fillon : exonération patronale pour brut ≤ 1,6 SMIC (taux max 31,94 %)
function computeFillonReduction(brutAnnuel: number): number {
  if (brutAnnuel <= 0) return 0;
  const smicAnnuel = SMIC_MENSUEL * 12;
  const seuilFillon = 1.6 * smicAnnuel;
  if (brutAnnuel >= seuilFillon) return 0;
  const T = (FILLON_MAX_RATE / 0.6) * (seuilFillon / brutAnnuel - 1);
  return brutAnnuel * Math.max(0, Math.min(T, FILLON_MAX_RATE));
}

export function estimateFiscalBreakdown(input: FiscalScenarioInput): FiscalBreakdown {
  const revenue = clampPositive(input.revenue);
  const charges = clampPositive(input.charges);
  const status = input.status;
  const strategy = resolveStrategy(status, input.taxStrategy);
  const baseOperatingProfit = revenue - charges;
  const salaryNetTarget = clampPositive(input.salaryNetTarget || 0);
  const dividendPayoutPct = Math.min(Math.max(input.dividendPayoutPct ?? 100, 0), 100) / 100;
  const householdSituation = input.householdSituation || 'celibataire';
  const children = input.children ?? 0;
  const householdParts = computeHouseholdParts(householdSituation, children);
  const dividendTaxOption = input.dividendTaxOption || 'pfu';
  const assumptions: string[] = [];

  let managementFees = 0;
  let payrollCost = 0;
  let socialContributions = 0;
  let netSalaryEstimate = 0;
  let corporateTax = 0;
  let reducedCorporateTax = 0;
  let standardCorporateTax = 0;
  let distributableAfterCorporateTax = 0;
  let dividendGross = 0;
  let pfu = 0;
  let dividendNet = 0;
  let prelevementsSociaux = 0;
  let taxePuma = 0;
  let eiSocialRate = 0;
  let eiCfpAmount = 0;
  let acreReduction = 0;
  let versementLiberatoireAmount = 0;
  let fillonReduction = 0;
  let cotisationMinSSI = 0;
  let regimeReel = false;
  let microDepassementSeuil = false;

  const vatThreshold = status === 'ei'
    ? (input.eiCategory === 'bic-vente' ? VAT_THRESHOLD_BIC : VAT_THRESHOLD_BNC)
    : null;

  const estimatedCfe = estimateCfe(status, revenue);

  if (status === 'sasu') {
    const targetSalaryShare = strategy === 'remuneration' ? 1 : strategy === 'mixte' ? 0.5 : 0;
    const fallbackNet = strategy === 'remuneration' || strategy === 'mixte'
      ? salaryNetTarget || Math.max(0, baseOperatingProfit * targetSalaryShare * 0.55)
      : 0;
    netSalaryEstimate = clampPositive(fallbackNet);

    if (netSalaryEstimate > 0) {
      // net/brut ≈ 0.77 (22-23 % charges salariales), coût patronal ≈ 42 % du brut
      const grossAnnuel = netSalaryEstimate / 0.77;
      fillonReduction = computeFillonReduction(grossAnnuel);
      payrollCost = clampPositive(grossAnnuel * 1.42 - fillonReduction);
      socialContributions = Math.max(0, payrollCost - netSalaryEstimate);
    }

    const tax = computeCorporateTax(baseOperatingProfit - payrollCost);
    corporateTax = tax.corporateTax;
    reducedCorporateTax = tax.reducedCorporateTax;
    standardCorporateTax = tax.standardCorporateTax;
    distributableAfterCorporateTax = clampPositive(baseOperatingProfit - payrollCost - corporateTax);
    dividendGross = distributableAfterCorporateTax * (strategy === 'remuneration' ? 0 : dividendPayoutPct);

    if (dividendGross > 0) {
      if (dividendTaxOption === 'bareme') {
        prelevementsSociaux = dividendGross * PS_RATE;
        dividendNet = dividendGross - prelevementsSociaux;
        assumptions.push('Dividendes : barème progressif IR (abattement 40 %) + prélèvements sociaux 18,6 %.');
      } else {
        pfu = dividendGross * PFU_RATE;
        dividendNet = dividendGross - pfu;
        assumptions.push('Dividendes : PFU forfaitaire de 31,4 % (12,8 % IR + 18,6 % prélèvements sociaux).');
      }
    }

    if (netSalaryEstimate < 9612 && dividendGross > 24030) {
      taxePuma = Math.max(0, dividendGross - 24030) * 0.065;
      assumptions.push('Taxe PUMA : revenus activite < 9 612 EUR et dividendes > 24 030 EUR, 6,5 % sur excedent.');
    }

    assumptions.push('SASU : IS a 15 % jusqu\'à 42 500 EUR puis 25 % au-delà.');
    if (payrollCost > 0) {
      if (fillonReduction > 0) {
        assumptions.push(`Réduction Fillon appliquée : ${Math.round(fillonReduction).toLocaleString('fr-FR')} EUR d'exonération patronale.`);
      } else {
        assumptions.push('Salaire SASU estimé avec un net proche de 55 % du coût employeur.');
      }
    }
    if (taxePuma > 0) assumptions.push('Taxe PUMA (cotisation subsidiaire maladie) incluse.');
  } else if (status === 'eurl') {
    const targetSalaryShare = strategy === 'remuneration' ? 1 : strategy === 'mixte' ? 0.6 : 0;
    const fallbackNet = strategy === 'remuneration' || strategy === 'mixte'
      ? salaryNetTarget || Math.max(0, baseOperatingProfit * targetSalaryShare * 0.69)
      : 0;
    netSalaryEstimate = clampPositive(fallbackNet);
    payrollCost = netSalaryEstimate > 0 ? netSalaryEstimate / 0.69 : 0;
    socialContributions = Math.max(0, payrollCost - netSalaryEstimate);

    if (socialContributions < COTISATION_MIN_SSI_ANNUELLE) {
      cotisationMinSSI = COTISATION_MIN_SSI_ANNUELLE - socialContributions;
      socialContributions = COTISATION_MIN_SSI_ANNUELLE;
      payrollCost = netSalaryEstimate + socialContributions;
      assumptions.push(`Cotisation minimale SSI appliquée : ${COTISATION_MIN_SSI_ANNUELLE.toLocaleString('fr-FR')} EUR/an minimum.`);
    }

    const tax = computeCorporateTax(baseOperatingProfit - payrollCost);
    corporateTax = tax.corporateTax;
    reducedCorporateTax = tax.reducedCorporateTax;
    standardCorporateTax = tax.standardCorporateTax;
    distributableAfterCorporateTax = clampPositive(baseOperatingProfit - payrollCost - corporateTax);
    dividendGross = distributableAfterCorporateTax * (strategy === 'remuneration' ? 0 : dividendPayoutPct);

    if (dividendGross > 0) {
      if (dividendTaxOption === 'bareme') {
        prelevementsSociaux = dividendGross * PS_RATE;
        dividendNet = dividendGross - prelevementsSociaux;
        assumptions.push('Dividendes : barème progressif IR (abattement 40 %) + prélèvements sociaux 18,6 %.');
      } else {
        pfu = dividendGross * PFU_RATE;
        dividendNet = dividendGross - pfu;
        assumptions.push('Dividendes traités au PFU (31,4 %).');
      }
    }

    assumptions.push('EURL à l\'IS avec rémunération TNS. Cotisations ~45 % du net (taux 2026).');
  } else if (status === 'ei') {
    const cat = input.eiCategory || 'bnc-liberal';
    const rates = EI_CATEGORY_RATES[cat];
    eiSocialRate = rates.urssaf;
    regimeReel = input.regimeReel === true;

    if (!regimeReel) {
      const microPlafond = cat === 'bic-vente' ? MICRO_PLAFOND_BIC_VENTE : MICRO_PLAFOND_BNC;
      microDepassementSeuil = revenue > microPlafond;
      if (microDepassementSeuil) {
        assumptions.push(`⚠ CA dépasse le plafond micro (${microPlafond.toLocaleString('fr-FR')} €) — régime réel obligatoire.`);
      }
    }

    // Régime réel : cotisations sur bénéfice (CA − charges). Micro : cotisations sur CA.
    const cotisBase = regimeReel ? clampPositive(baseOperatingProfit) : revenue;
    eiCfpAmount = cotisBase * rates.cfp;

    const acreActive = input.acre === true;
    const effectiveSocialRate = acreActive ? eiSocialRate * 0.5 : eiSocialRate;
    const socialChargeAmount = cotisBase * effectiveSocialRate;
    acreReduction = acreActive ? cotisBase * eiSocialRate * 0.5 : 0;

    socialContributions = socialChargeAmount + eiCfpAmount;

    if (!regimeReel && input.versementLiberatoire) {
      const vlRates: Record<EiCategory, number> = {
        'bic-vente': 0.01,
        'bic-services': 0.017,
        'bnc-liberal': 0.022,
        'bnc-cipav': 0.022,
      };
      versementLiberatoireAmount = revenue * (vlRates[cat] || 0.022);
    }

    netSalaryEstimate = clampPositive(baseOperatingProfit - socialContributions - versementLiberatoireAmount - estimatedCfe);

    if (regimeReel) {
      assumptions.push(`EI Régime réel : cotisations a ${(eiSocialRate * 100).toFixed(1)} % du bénéfice (CA − charges).`);
    } else {
      assumptions.push(`EI / Micro : cotisations a ${(eiSocialRate * 100).toFixed(1)} % du CA (${cat}).`);
    }
    if (acreActive) assumptions.push('ACRE : reduction de 50 % des cotisations 1ere annee.');
    if (input.versementLiberatoire) assumptions.push('Versement liberatoire : IR paye avec les cotisations.');
    if (!input.versementLiberatoire) assumptions.push('IR calcule via le quotient familial (bareme progressif).');
    assumptions.push(`CFE estimee a ~${Math.round(estimatedCfe)} EUR/an.`);
  } else if (status === 'portage') {
    managementFees = revenue * (input.managementFeesOverride ?? 0.08);
    const payrollBase = Math.max(0, revenue - charges - managementFees);
    socialContributions = payrollBase * 0.46;
    netSalaryEstimate = clampPositive(payrollBase - socialContributions);
    assumptions.push('Portage : frais de gestion 8 % du CA.');
    assumptions.push('Portage : salaire net apres charges (46 %).');
  }

  const operatingProfit = baseOperatingProfit - managementFees;
  const estimatedTakeHome = clampPositive(netSalaryEstimate + dividendNet);
  const estimatedVatRemaining = !input.vatRegistered && vatThreshold
    ? Math.max(0, vatThreshold - clampPositive(input.caYtd || 0))
    : vatThreshold;

  let incomeTaxBrut = 0;
  let incomeTaxDecote = 0;
  let incomeTaxNet = 0;

  // Base IR selon statut :
  // - EI micro avec VL : IR déjà payé dans le versement libératoire → base = 0
  // - EI micro sans VL : base = CA × (1 − abattement forfaitaire), pas le net après cotisations
  // - EI régime réel / autres : base = salaire net (ou salaire + div×60% au barème)
  let baseTaxableIR: number;
  if (status === 'ei' && !regimeReel && input.versementLiberatoire) {
    baseTaxableIR = 0;
  } else if (status === 'ei' && !regimeReel) {
    const eiCat = input.eiCategory || 'bnc-liberal';
    baseTaxableIR = revenue * (1 - MICRO_ABATTEMENTS[eiCat]);
  } else if (dividendTaxOption === 'bareme' && dividendGross > 0) {
    baseTaxableIR = netSalaryEstimate + dividendGross * 0.60;
  } else {
    baseTaxableIR = netSalaryEstimate;
  }

  if (baseTaxableIR > 0) {
    const result = computeIncomeTax(baseTaxableIR, householdParts, householdSituation, children);
    incomeTaxBrut = result.impotBrut;
    incomeTaxDecote = result.decote;
    incomeTaxNet = result.impotNet;

    assumptions.push(`IR calcule avec ${householdParts} part${householdParts > 1 ? 's' : ''} fiscale${householdParts > 1 ? 's' : ''} (${householdSituation}, ${children} enfant${children !== 1 ? 's' : ''}).`);
    if (incomeTaxDecote > 0) assumptions.push(`Decote appliquee : -${Math.round(incomeTaxDecote)} EUR d'IR.`);
    if (incomeTaxNet === 0 && incomeTaxBrut === 0) assumptions.push('IR nul : revenu imposable en dessous du seuil d\'imposition.');
  }

  const estimatedTakeHomeAfterIr = clampPositive(estimatedTakeHome - incomeTaxNet - taxePuma);
  const { tauxMarginalIR, resteAvantTrancheSuivanteIR } = baseTaxableIR > 0
    ? computeMarginalRate(baseTaxableIR, householdParts)
    : { tauxMarginalIR: 0, resteAvantTrancheSuivanteIR: 0 };

  const confidence = selectFinancialConfidence({
    status, revenue, eiCategory: input.eiCategory, taxStrategy: input.taxStrategy,
  });

  return {
    status, strategy, revenue, charges, operatingProfit,
    managementFees, payrollCost, socialContributions,
    netSalaryEstimate, corporateTax, reducedCorporateTax, standardCorporateTax,
    distributableAfterCorporateTax, dividendGross, pfu, dividendNet,
    estimatedTakeHome, estimatedTakeHomeAfterIr,
    estimatedVatRemaining, estimatedCfe, taxePuma,
    eiSocialRate, eiCfpAmount, acreReduction, versementLiberatoireAmount,
    householdParts, incomeTaxBrut, incomeTaxDecote, incomeTaxNet,
    householdSituation, children,
    assumptions,
    fillonReduction, dividendTaxOption, prelevementsSociaux, cotisationMinSSI, regimeReel,
    tauxMarginalIR, resteAvantTrancheSuivanteIR, microDepassementSeuil,
    confidence,
  };
}

export function getStatusLabel(status: TreasuryProfile['status']): string {
  if (status === 'sasu') return 'SASU';
  if (status === 'eurl') return 'EURL';
  if (status === 'portage') return 'Portage';
  return 'EI / Micro';
}
